import { Theme } from '@logto/schemas';
import { displayable, formatRgb, oklch as toOklch, wcagContrast, type Oklch } from 'culori';
import { useEffect, useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';

/**
 * Drives HeroUI's `--accent` from the configured branding colour.
 *
 * Only the hue and saturation of the configured colour are kept. Lightness starts at the theme's
 * target and steps down until white text clears WCAG AA, so every brand hue gets the same
 * white-on-accent CTA instead of some hues flipping to dark text.
 */
const TARGET_LIGHTNESS = { light: 0.55, dark: 0.6 } as const;
const MIN_LIGHTNESS = 0.4;
const DEFAULT_ACCENT = { c: 0.195, h: 253.83 } as const;
const SNOW = 'oklch(0.9911 0 0)';

const maxChroma = (lightness: number, hue: number): number => {
  const search = (low: number, high: number, steps: number): number => {
    if (steps === 0) {
      return low;
    }

    const mid = (low + high) / 2;

    return displayable({ mode: 'oklch', l: lightness, c: mid, h: hue })
      ? search(mid, high, steps - 1)
      : search(low, mid, steps - 1);
  };

  return search(0, 0.4, 16);
};

/**
 * Chroma at a new lightness that keeps the source's share of the gamut. A light dark-mode brand
 * tint (e.g. a 400 shade) carries little absolute chroma; kept as-is at CTA lightness it turns
 * dusty instead of reading as the same brand colour.
 */
const chromaAt = (source: Oklch, lightness: number, saturation: number): number => {
  const ceiling = maxChroma(lightness, source.h ?? 0);

  return Math.min(ceiling, Math.max(source.c, saturation * ceiling));
};

const darkenUntilReadable = (source: Oklch, saturation: number, lightness: number): Oklch => {
  const color: Oklch = { ...source, l: lightness, c: chromaAt(source, lightness, saturation) };

  return lightness > MIN_LIGHTNESS && wcagContrast(color, SNOW) < 4.5
    ? darkenUntilReadable(source, saturation, lightness - 0.01)
    : color;
};

const INK = 'oklch(0.21 0.006 285.89)';
const ACHROMATIC_CHROMA = 0.03;

type Accent = { color: Oklch; foreground: string };

const resolveAccent = (color: string | undefined, isDark: boolean): Accent => {
  const parsed = color ? toOklch(color) : undefined;

  // A black, white or grey brand has no hue to keep, and pinning its lightness yields a mid
  // grey that reads as a disabled button. Go monochrome instead: ink in light, snow in dark.
  if (parsed && parsed.c < ACHROMATIC_CHROMA) {
    return isDark
      ? { color: { mode: 'oklch', l: 0.93, c: 0, h: 0 }, foreground: INK }
      : { color: { mode: 'oklch', l: Math.min(parsed.l, 0.25), c: 0, h: 0 }, foreground: SNOW };
  }

  const source: Oklch = parsed ?? { mode: 'oklch', l: 0.55, ...DEFAULT_ACCENT };
  const sourceCeiling = maxChroma(source.l, source.h ?? 0);
  const saturation = sourceCeiling > 0 ? Math.min(1, source.c / sourceCeiling) : 0;

  return {
    color: darkenUntilReadable(source, saturation, TARGET_LIGHTNESS[isDark ? 'dark' : 'light']),
    foreground: SNOW,
  };
};

const useColorTheme = () => {
  const { theme, experienceSettings } = useContext(PageContext);
  const { primaryColor, darkPrimaryColor } = experienceSettings?.color ?? {};

  useEffect(() => {
    const isDark = theme === Theme.Dark;
    const accent = resolveAccent(
      isDark && darkPrimaryColor ? darkPrimaryColor : primaryColor,
      isDark
    );

    // Set on <html>, not <body>: HeroUI computes --accent-hover / --accent-soft on :root, and a
    // custom property is resolved where it is declared, so an override lower down never
    // reaches them.
    const { style } = document.documentElement;
    style.setProperty('--accent', formatRgb(accent.color));
    style.setProperty('--accent-foreground', accent.foreground);
  }, [theme, primaryColor, darkPrimaryColor]);
};

export default useColorTheme;
